<template>
  <div class="flex flex-col gap-4">
    <!-- Header -->
    <TextHeader class="flex items-center justify-between">
      Inzichten
      <span class="text-base font-normal lg:hidden">
        <ExportComponent
          :small="!mdAndLarger"
          :metering-point="meteringPoint"
          :date-range="filterStore.dateRange"
          :type="exportType"
        />
      </span>
    </TextHeader>
    <!-- Filter bar -->
    <ToolbarComponent uncollapsed no-mode with-portfolio>
      <div class="ml-auto">
        <ExportComponent
          v-if="lgAndLarger"
          :metering-point="meteringPoint"
          :date-range="filterStore.dateRange"
          :type="exportType"
        />
      </div>
    </ToolbarComponent>
    <MessageComponent
      v-if="filterStore.currentMeteringPoint?.demo"
      color="info"
    >
      {{ $p("demo-disclaimer") }}
    </MessageComponent>
    <div class="flex flex-col gap-4">
      <!-- Empty state when no metering point is selected -->
      <BaseCard
        v-if="!meteringPoint"
        class="flex h-40 items-center justify-center italic"
      >
        <p>Maak een selectie om inzichten te kunnen bekijken</p>
      </BaseCard>
      <template v-else>
        <!--
        Indicative yields. Optima benefit and own portfolio benefit are shown as
        compact boxes below the chart.
      -->
        <InsightsChartPanel
          v-if="visibility.assetResults"
          :metering-point="meteringPoint"
          :date-range="filterStore.dateRange"
        >
          <template #extras>
            <div class="flex flex-wrap items-start gap-4">
              <OptimaInsightPanel
                v-if="visibility.optima"
                compact
                :metering-point="meteringPoint"
                :date-range="filterStore.dateRange"
              />
              <OwnPortfolioBenefitInsightPanel
                v-if="visibility.ownPortfolioBenefit"
                compact
                :metering-point="meteringPoint"
                :date-range="filterStore.dateRange"
              />
            </div>
          </template>
        </InsightsChartPanel>
        <!--
        Detailed results table (same as the legacy results page for now),
        preceded by the drawer explaining how to use it.
      -->
        <TableExplainerComponent v-if="visibility.assetResults" />
        <ResultsTablePanel
          v-if="visibility.assetResults"
          :metering-point="meteringPoint"
          :date-range="filterStore.dateRange"
          :mode="MODE.BOTH"
          as-insights
          @zoom="filterStore.setDateRange"
        />
        <!-- Curtailment chart -->
        <CurtailmentInsightPanel
          v-if="visibility.curtailment"
          :metering-point="meteringPoint"
          :date-range="filterStore.dateRange"
        />
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue"
import { startOfMonth, lastDayOfMonth, startOfToday } from "date-fns"
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core"
import $p from "@/services/phraseService.js"
import { MODE } from "@/helpers/filter.js"
import useFilterStore from "@/stores/filterStore.js"
import {
  PORTFOLIO,
  ASSET,
} from "@/stores/repower/meteringPointStoreFactories.js"
import { EXPORT_TYPES } from "@/stores/repower/exportsStore.js"
import { TextHeader, BaseCard } from "@repowerednl/ui-component-library"
import ToolbarComponent from "@/components/navigation/ToolbarComponent.vue"
import ExportComponent from "@/components/information/ExportComponent.vue"
import MessageComponent from "@/components/information/MessageComponent.vue"
import TableExplainerComponent from "@/components/information/TableExplainerComponent.vue"
import ResultsTablePanel from "@/components/panels/ResultsTablePanel.vue"
import InsightsChartPanel from "@/components/panels/insights/InsightsChartPanel.vue"
import OptimaInsightPanel from "@/components/panels/insights/OptimaInsightPanel.vue"
import OwnPortfolioBenefitInsightPanel from "@/components/panels/insights/OwnPortfolioBenefitInsightPanel.vue"
import CurtailmentInsightPanel from "@/components/panels/insights/CurtailmentInsightPanel.vue"
import useDefaultDateRange from "@/composables/useDefaultDateRange.js"
import usePortfolioAsDefaultMeteringPoint from "@/composables/usePortfolioAsDefaultMeteringPoint.js"

const filterStore = useFilterStore()
const breakpoints = useBreakpoints(breakpointsTailwind)
const mdAndLarger = breakpoints.greaterOrEqual("md")
const lgAndLarger = breakpoints.greaterOrEqual("lg")

// Insights default to the current month, matching the results page.
useDefaultDateRange([
  startOfMonth(startOfToday()),
  lastDayOfMonth(startOfToday()),
])
usePortfolioAsDefaultMeteringPoint()

const meteringPoint = computed(() => filterStore.currentMeteringPoint)

/**
 * Legacy results from before 2025 require a different export type.
 */
const exportType = computed(() =>
  filterStore.useLegacyResults
    ? EXPORT_TYPES.RECORD_STREAMS
    : EXPORT_TYPES.RECORDS,
)

/**
 * Visibility rules per insight: is this insight meaningful for the selected
 * level (portfolio / allocation point / asset) at all? This is the cheap
 * upfront filter only — what happens on empty data is up to each panel. The
 * two benefit boxes hide themselves, the two charts keep their card and show
 * an empty message instead.
 *
 * Note that the API mixes casing in `flex_services`: the imbalance key comes
 * from the FlexServiceType enum, the CBC one is a hardcoded string.
 */
const visibility = computed(() => {
  const mp = meteringPoint.value
  if (!mp) {
    return {}
  }
  const role = mp.role
  const flex = mp.properties?.flex_services ?? {}
  return {
    assetResults: true,
    optima: role === ASSET || role === PORTFOLIO,
    ownPortfolioBenefit: role === PORTFOLIO,
    curtailment:
      role !== ASSET ||
      Boolean(
        flex.IMBALANCE_CURTAILMENT?.is_active ||
        flex.cbc_curtailment?.is_active,
      ),
  }
})
</script>
